import { useState, useCallback, useEffect } from 'react';
import { Stack, Text, Group, Button, Alert, Loader, TextInput, ActionIcon } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import type { Partner } from '@ayocore/erp/crm/schemas/partner.schema';

export type MergePartnerFormProps = {
  /** Текущий партнёр — мастер (получатель). */
  masterId: number;
  onSuccess: () => void;
  /** Если известен дубль — пропускаем шаг поиска и сразу грузим превью. */
  duplicateId?: number;
};

type MergePreview = Record<string, number>;

const PREVIEW_LABELS: Record<string, string> = {
  contacts: 'Контакты организации',
  contactPersons: 'Контактные лица',
  leads: 'Лиды',
  deals: 'Сделки',
  documents: 'Документы',
  contracts: 'Договоры',
  tasks: 'Задачи',
  tags: 'Теги',
  segments: 'Сегменты',
  events: 'События',
};

/**
 * Контент модалки объединения дублей партнёров. Открывается через useModal.
 * Одноэкранный flow: поиск дубля → превью → подтверждение.
 * `onSuccess` должен сам закрыть модалку (modal.close()) и обновить данные.
 */
export function MergePartnerForm({ masterId, onSuccess, duplicateId }: MergePartnerFormProps) {
  const partnerRepository = usePartnerRepository();

  const [master, setMaster] = useState<Partner | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Partner[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  const [selectedDuplicate, setSelectedDuplicate] = useState<Partner | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmStep, setConfirmStep] = useState(false);

  // Выбор дубля → загрузка превью
  const handleSelectDuplicate = useCallback(
    async (partner: Partner) => {
      setSelectedDuplicate(partner);
      setSearchResults([]);
      setSearchQuery('');
      setError(null);
      setConfirmStep(false);
      try {
        setPreviewLoading(true);
        const result = await partnerRepository.getMergePreview(partner.id);
        setPreview(result as MergePreview);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка загрузки превью');
      } finally {
        setPreviewLoading(false);
      }
    },
    [partnerRepository]
  );

  /** При монтировании загружаем мастера и (если передан) дубля по ID */
  useEffect(() => {
    void (async () => {
      try {
        const masterData = await partnerRepository.findById(masterId);
        if (masterData) setMaster(masterData);

        if (duplicateId) {
          const duplicateData = await partnerRepository.findById(duplicateId);
          if (duplicateData) void handleSelectDuplicate(duplicateData as Partner);
        }
      } catch {
        // ignore — master name will fallback to ID
      }
    })();
  }, [masterId, duplicateId]);

  // Поиск партнёров по Enter
  const handleSearch = useCallback(async () => {
    const query = searchQuery.trim();
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      setSearchLoading(true);
      const results = await partnerRepository.search(query).limit(10).fetch();
      setSearchResults(results.filter((p: Partner) => p.id !== masterId));
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [partnerRepository, masterId, searchQuery]);

  // Объединение
  const handleMerge = useCallback(async () => {
    if (!selectedDuplicate) return;
    try {
      setMerging(true);
      setError(null);
      await partnerRepository.mergeDuplicates(selectedDuplicate.id, masterId);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка объединения');
    } finally {
      setMerging(false);
    }
  }, [selectedDuplicate, masterId, partnerRepository, onSuccess]);

  const totalCount = preview
    ? Object.entries(preview)
        .filter(([key]) => key !== 'notes')
        .reduce((sum, [, count]) => sum + count, 0)
    : 0;

  return (
    <Stack gap='sm'>
      {error && (
        <Alert color='red' withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Text size='xs' c='dimmed'>
        Мастер (остаётся):{' '}
        <Text span fw={500} c='dark'>
          {master?.name || `#${masterId}`}
        </Text>{' '}
        (ID {masterId})
      </Text>

      {/* Выбор дубля */}
      {!selectedDuplicate && (
        <>
          <TextInput
            placeholder='Название, ИНН или ID… (Enter для поиска)'
            size='xs'
            value={searchQuery}
            onChange={e => setSearchQuery(e.currentTarget.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                void handleSearch();
              }
            }}
            rightSection={
              searchLoading ? (
                <Loader size={14} />
              ) : (
                <ActionIcon
                  size='sm'
                  variant='subtle'
                  onClick={() => void handleSearch()}
                  aria-label='Искать'
                >
                  <IconSearch size={14} />
                </ActionIcon>
              )
            }
          />
          {searchResults.length > 0 && (
            <Stack gap={2} style={{ maxHeight: 200, overflow: 'auto' }}>
              {searchResults.map(p => (
                <Button
                  key={p.id}
                  variant='subtle'
                  size='xs'
                  justify='flex-start'
                  fullWidth
                  onClick={() => void handleSelectDuplicate(p)}
                  styles={{ label: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                >
                  {p.name} {p.inn ? `(ИНН: ${p.inn})` : ''} — ID {p.id}
                </Button>
              ))}
            </Stack>
          )}
        </>
      )}

      {/* Превью */}
      {selectedDuplicate && (
        <>
          <Group gap='xs' align='center'>
            <Text size='xs' c='dimmed'>
              Дубль (будет удалён):
            </Text>
            <Text size='xs' fw={500}>
              {selectedDuplicate.name} (ID {selectedDuplicate.id})
            </Text>
            <Button
              variant='subtle'
              size='compact-xs'
              color='gray'
              onClick={() => {
                setSelectedDuplicate(null);
                setPreview(null);
                setConfirmStep(false);
              }}
            >
              Изменить
            </Button>
          </Group>

          {previewLoading ? (
            <Loader size='sm' />
          ) : preview ? (
            <>
              <Text size='xs' fw={500} mt='xs'>
                Будет перенесено:
              </Text>
              <Stack gap={2}>
                {Object.entries(PREVIEW_LABELS).map(([key, label]) => {
                  const count = preview[key] ?? 0;
                  if (count === 0) return null;
                  return (
                    <Text key={key} size='xs' c='dimmed'>
                      • {label}: {count}
                    </Text>
                  );
                })}
                {preview.notes ? (
                  <Text size='xs' c='dimmed'>
                    • Реквизиты и описание (будут дополнены)
                  </Text>
                ) : null}
                {totalCount === 0 && !preview.notes && (
                  <Text size='xs' c='dimmed'>
                    Нет данных для переноса
                  </Text>
                )}
              </Stack>

              <Alert color='orange' variant='light' mt='xs'>
                <Text size='xs'>
                  Партнёр-дубль будет удалён после объединения. Это действие нельзя отменить.
                </Text>
              </Alert>

              {!confirmStep ? (
                <Group justify='flex-end' pt='sm'>
                  <Button size='xs' color='orange' onClick={() => setConfirmStep(true)}>
                    Объединить
                  </Button>
                </Group>
              ) : (
                <Group justify='flex-end' pt='sm'>
                  <Button variant='default' size='xs' onClick={() => setConfirmStep(false)}>
                    Назад
                  </Button>
                  <Button
                    size='xs'
                    color='red'
                    loading={merging}
                    disabled={merging}
                    onClick={() => void handleMerge()}
                  >
                    Да, объединить партнёров
                  </Button>
                </Group>
              )}
            </>
          ) : null}
        </>
      )}
    </Stack>
  );
}
